import React, { useState, useRef, useCallback, useMemo } from 'react';
import { BUILTIN_MAPS } from '../../../games/stumble-home/maps';

// ── Types ─────────────────────────────────────────────────────────────────────

type TileType = 'normal' | 'start' | 'end' | 'wall' | 'hazard1' | 'hazard2' | 'free';

interface StumbleMap {
  id: string;
  name: string;
  cols: number;
  rows: number;
  tiles: TileType[][];
}

interface SavedMap {
  name: string;
  timestamp: number;
  map: StumbleMap;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'stumble-home-maps';
const SQRT3_2 = Math.sqrt(3) / 2;

const TILE_TYPES: TileType[] = ['normal', 'start', 'end', 'wall', 'hazard1', 'hazard2', 'free'];

const TILE_COLORS: Record<TileType, string> = {
  normal:  '#d1d5db',
  start:   '#22c55e',
  end:     '#eab308',
  wall:    '#374151',
  hazard1: '#f97316',
  hazard2: '#ef4444',
  free:    '#06b6d4',
};

const TILE_LABELS: Record<TileType, string> = {
  normal:  'Normal',
  start:   'Start',
  end:     'End',
  wall:    'Wall',
  hazard1: 'Hazard 1',
  hazard2: 'Hazard 2',
  free:    'Free Move',
};

const TILE_ABBR: Record<TileType, string> = {
  normal: '', start: 'S', end: 'E', wall: 'W',
  hazard1: 'H1', hazard2: 'H2', free: 'F',
};

const TILE_DESC: Record<TileType, string> = {
  normal:  'Open tile, no effect',
  start:   'Meeple starting position',
  end:     'Destination — reaching wins',
  wall:    'Impassable — meeple bounces',
  hazard1: 'All discard 1 card on entry',
  hazard2: 'All discard 2 cards on entry',
  free:    'Move meeple to any adjacent tile',
};

// ── Geometry ──────────────────────────────────────────────────────────────────

// Each triangle is "up" when (row + col) % 2 === 0.
// Upward △:  top = ((c+1)b/2, rh)   BL = (cb/2, (r+1)h)   BR = ((c+2)b/2, (r+1)h)
// Downward ▽: TL  = (cb/2, rh)       TR  = ((c+2)b/2, rh)   bot = ((c+1)b/2, (r+1)h)
//
// Edge colors by angle of line family:
//   Black (0°)  = horizontal edge  → bottom of △, top of ▽
//   Blue  (60°) = right-slanting   → top→BR of △, TL→bot of ▽
//   Red   (120°)= left-slanting    → top→BL of △, TR→bot of ▽

function triH(b: number): number { return b * SQRT3_2; }

function triVerts(r: number, c: number, b: number): [number, number][] {
  const h = triH(b);
  const x0 = c * b / 2;
  const y0 = r * h;
  return (r + c) % 2 === 0
    ? [[x0 + b / 2, y0], [x0, y0 + h], [x0 + b, y0 + h]]      // △ top, BL, BR
    : [[x0, y0], [x0 + b, y0], [x0 + b / 2, y0 + h]];          // ▽ TL, TR, bot
}

type Edge = { color: string; x1: number; y1: number; x2: number; y2: number };

function triEdges(r: number, c: number, b: number): Edge[] {
  const h = triH(b);
  const x0 = c * b / 2;
  const y0 = r * h;
  if ((r + c) % 2 === 0) {
    const top: [number, number] = [x0 + b / 2, y0];
    const bl:  [number, number] = [x0,          y0 + h];
    const br:  [number, number] = [x0 + b,      y0 + h];
    return [
      { color: '#111827', x1: bl[0],  y1: bl[1],  x2: br[0],  y2: br[1]  }, // black
      { color: '#2563eb', x1: top[0], y1: top[1], x2: br[0],  y2: br[1]  }, // blue
      { color: '#dc2626', x1: top[0], y1: top[1], x2: bl[0],  y2: bl[1]  }, // red
    ];
  } else {
    const tl:  [number, number] = [x0,          y0];
    const tr:  [number, number] = [x0 + b,      y0];
    const bot: [number, number] = [x0 + b / 2,  y0 + h];
    return [
      { color: '#111827', x1: tl[0],  y1: tl[1],  x2: tr[0],  y2: tr[1]  }, // black
      { color: '#2563eb', x1: tl[0],  y1: tl[1],  x2: bot[0], y2: bot[1] }, // blue
      { color: '#dc2626', x1: tr[0],  y1: tr[1],  x2: bot[0], y2: bot[1] }, // red
    ];
  }
}

function centroid(verts: [number, number][]): [number, number] {
  return [
    verts.reduce((s, v) => s + v[0], 0) / verts.length,
    verts.reduce((s, v) => s + v[1], 0) / verts.length,
  ];
}

function cross(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  return (ax - cx) * (by - cy) - (bx - cx) * (ay - cy);
}

function pointInTri(px: number, py: number, v: [number, number][]): boolean {
  const d1 = cross(px, py, v[0][0], v[0][1], v[1][0], v[1][1]);
  const d2 = cross(px, py, v[1][0], v[1][1], v[2][0], v[2][1]);
  const d3 = cross(px, py, v[2][0], v[2][1], v[0][0], v[0][1]);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

function hitTest(px: number, py: number, b: number, cols: number, rows: number): [number, number] | null {
  const h = triH(b);
  const r = Math.floor(py / h);
  if (r < 0 || r >= rows) return null;
  const cStart = Math.floor(px / (b / 2)) - 1;
  for (let dc = 0; dc <= 3; dc++) {
    const c = cStart + dc;
    if (c < 0 || c >= cols) continue;
    if (pointInTri(px, py, triVerts(r, c, b))) return [r, c];
  }
  return null;
}

// Scale triangle base so the grid fills roughly 720px on the longest axis, capped 40–90.
function computeB(cols: number, rows: number): number {
  const TARGET = 720;
  const bW = (TARGET * 2) / (cols + 1);
  const bH = TARGET / (rows * SQRT3_2);
  return Math.max(40, Math.min(90, bW, bH));
}

// ── Map helpers ───────────────────────────────────────────────────────────────

function makeMap(cols: number, rows: number, name = 'New Map'): StumbleMap {
  return {
    id: crypto.randomUUID(),
    name,
    cols,
    rows,
    tiles: Array.from({ length: rows }, () => Array<TileType>(cols).fill('normal')),
  };
}

function resizeMap(map: StumbleMap, cols: number, rows: number): StumbleMap {
  return {
    ...map,
    cols,
    rows,
    tiles: Array.from({ length: rows }, (_, r) =>
      Array.from({ length: cols }, (_, c) =>
        r < map.rows && c < map.cols ? map.tiles[r][c] : 'normal'
      )
    ),
  };
}

// ── Persistence ───────────────────────────────────────────────────────────────

function getSavedMaps(): SavedMap[] {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); }
  catch { return []; }
}

function persistMap(map: StumbleMap): void {
  const all = getSavedMaps().filter(m => m.name !== map.name);
  all.unshift({ name: map.name, timestamp: Date.now(), map });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all.slice(0, 50)));
}

function removeSavedMap(name: string): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(getSavedMaps().filter(m => m.name !== name)));
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function StumbleHomeMapMaker() {
  const [map, setMap]           = useState<StumbleMap>(() => makeMap(10, 6));
  const [activeTile, setActive] = useState<TileType>('wall');
  const [savedMaps, setSaved]   = useState<SavedMap[]>(getSavedMaps);
  const [modal, setModal]       = useState<'load' | 'export' | 'import' | null>(null);
  const [showPresets, setShowPresets] = useState(false);
  const [importText, setImport] = useState('');
  const [saveMsg, setSaveMsg]   = useState('');

  const isPainting = useRef(false);
  const svgRef     = useRef<SVGSVGElement>(null);
  const fileRef    = useRef<HTMLInputElement>(null);

  const b    = useMemo(() => computeB(map.cols, map.rows), [map.cols, map.rows]);
  const svgW = useMemo(() => (map.cols + 1) * b / 2, [map.cols, b]);
  const svgH = useMemo(() => map.rows * triH(b),      [map.rows, b]);

  // Convert a MouseEvent on the SVG into viewBox coordinates.
  const toSVGCoords = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current!.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width)  * svgW,
      y: ((e.clientY - rect.top)  / rect.height) * svgH,
    };
  }, [svgW, svgH]);

  const paint = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const { x, y } = toSVGCoords(e);
    const hit = hitTest(x, y, b, map.cols, map.rows);
    if (!hit) return;
    const [r, c] = hit;
    setMap(prev => {
      if (prev.tiles[r][c] === activeTile) return prev;
      const tiles = prev.tiles.map(row => [...row]);
      tiles[r][c] = activeTile;
      return { ...prev, tiles };
    });
  }, [toSVGCoords, b, map.cols, map.rows, activeTile]);

  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    isPainting.current = true;
    paint(e);
  };
  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isPainting.current) paint(e);
  };
  const stopPaint = () => { isPainting.current = false; };

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleSave = () => {
    if (!map.name.trim()) { setSaveMsg('Name required'); setTimeout(() => setSaveMsg(''), 2000); return; }
    persistMap(map);
    setSaved(getSavedMaps());
    setSaveMsg('Saved!');
    setTimeout(() => setSaveMsg(''), 2000);
  };

  const handleLoad = (saved: SavedMap) => { setMap(saved.map); setModal(null); };

  const handleDelete = (name: string) => {
    removeSavedMap(name);
    setSaved(getSavedMaps());
  };

  const handleImport = (text: string) => {
    try {
      const p = JSON.parse(text);
      if (!p.tiles || !Array.isArray(p.tiles) || !p.cols || !p.rows) throw new Error();
      setMap({ ...p, id: p.id ?? crypto.randomUUID() });
      setModal(null);
      setImport('');
    } catch {
      alert('Invalid map JSON — expected { name, cols, rows, tiles[][] }');
    }
  };

  const handleFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => handleImport(ev.target?.result as string);
    reader.readAsText(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(map, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(map.name || 'map').replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClear = () => {
    if (window.confirm('Clear the entire map?')) setMap(makeMap(map.cols, map.rows, map.name));
  };

  const handleLoadPreset = (preset: typeof BUILTIN_MAPS[number]) => {
    setMap({
      id: crypto.randomUUID(),
      name: preset.name,
      cols: preset.cols,
      rows: preset.rows,
      tiles: preset.tiles as TileType[][],
    });
    setShowPresets(false);
  };

  const exportJson = useMemo(() => JSON.stringify(map, null, 2), [map]);

  // ── Tile stats for sidebar ────────────────────────────────────────────────

  const tileCounts = useMemo(() => {
    const counts: Partial<Record<TileType, number>> = {};
    map.tiles.flat().forEach(t => { if (t !== 'normal') counts[t] = (counts[t] ?? 0) + 1; });
    return counts;
  }, [map.tiles]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className="flex flex-col h-screen bg-gray-900 text-white"
      style={{ userSelect: 'none' }}
      onMouseUp={stopPaint}
    >
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-2 bg-gray-800 border-b border-gray-700 shrink-0">
        <span className="font-bold text-white whitespace-nowrap">Stumble Home — Map Maker</span>

        <input
          className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm w-44 text-white"
          placeholder="Map name…"
          value={map.name}
          onChange={e => setMap(p => ({ ...p, name: e.target.value }))}
        />

        <label className="flex items-center gap-2 text-sm text-gray-300 whitespace-nowrap">
          Cols&nbsp;<span className="font-mono text-white w-6 inline-block">{map.cols}</span>
          <input
            type="range" min={4} max={24} value={map.cols}
            onChange={e => setMap(p => resizeMap(p, +e.target.value, p.rows))}
            className="w-28"
          />
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-300 whitespace-nowrap">
          Rows&nbsp;<span className="font-mono text-white w-6 inline-block">{map.rows}</span>
          <input
            type="range" min={2} max={14} value={map.rows}
            onChange={e => setMap(p => resizeMap(p, p.cols, +e.target.value))}
            className="w-28"
          />
        </label>

        <div className="flex gap-2 ml-auto flex-wrap items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium"
            >Save</button>
            {saveMsg && <span className="text-green-400 text-xs">{saveMsg}</span>}
          </div>
          <button
            onClick={() => { setSaved(getSavedMaps()); setModal('load'); }}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm"
          >Load</button>
          <button
            onClick={() => setModal('export')}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm"
          >Export</button>
          <button
            onClick={() => setModal('import')}
            className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm"
          >Import</button>
          <div className="relative">
            <button
              onClick={() => setShowPresets(p => !p)}
              className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-sm"
            >Presets</button>
            {showPresets && (
              <div className="absolute right-0 top-full mt-1 bg-gray-700 border border-gray-600 rounded shadow-xl z-50 min-w-48">
                {BUILTIN_MAPS.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => handleLoadPreset(preset)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-600 text-sm"
                  >
                    <div className="font-medium">{preset.name}</div>
                    <div className="text-xs text-gray-400">{preset.cols}×{preset.rows}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleClear}
            className="px-3 py-1 bg-red-800 hover:bg-red-700 rounded text-sm"
          >Clear</button>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* Legend / sidebar */}
        <div className="w-52 shrink-0 bg-gray-800 border-r border-gray-700 flex flex-col overflow-y-auto p-3 gap-1">
          <div className="text-xs font-semibold text-gray-400 uppercase mb-2">Tile Type</div>

          {TILE_TYPES.map(type => (
            <button
              key={type}
              onClick={() => setActive(type)}
              className={`flex items-start gap-2 px-2 py-2 rounded text-left text-sm transition-colors ${
                activeTile === type ? 'ring-2 ring-white bg-gray-700' : 'hover:bg-gray-700'
              }`}
            >
              <div
                className="w-5 h-5 rounded-sm shrink-0 mt-0.5 border border-gray-500"
                style={{ background: TILE_COLORS[type] }}
              />
              <div>
                <div className="font-medium leading-tight">{TILE_LABELS[type]}</div>
                <div className="text-gray-400 text-xs leading-snug">{TILE_DESC[type]}</div>
              </div>
            </button>
          ))}

          {/* Edge color key */}
          <div className="mt-4 pt-3 border-t border-gray-700">
            <div className="text-xs font-semibold text-gray-400 uppercase mb-2">Edge Colors</div>
            {[
              { color: '#111827', label: 'Black  0°'  },
              { color: '#2563eb', label: 'Blue   60°' },
              { color: '#dc2626', label: 'Red   120°' },
            ].map(({ color, label }) => (
              <div key={color} className="flex items-center gap-2 text-xs text-gray-300 mb-1">
                <svg width={24} height={6}>
                  <line x1={0} y1={3} x2={24} y2={3} stroke={color} strokeWidth={2} strokeLinecap="round" />
                </svg>
                <span className="font-mono">{label}</span>
              </div>
            ))}
          </div>

          {/* Map stats */}
          <div className="mt-4 pt-3 border-t border-gray-700 text-xs text-gray-400 space-y-0.5">
            <div className="font-semibold text-gray-300 mb-1">Map info</div>
            <div>{map.cols} × {map.rows} — {map.cols * map.rows} tiles</div>
            {(Object.entries(tileCounts) as [TileType, number][]).map(([t, n]) => (
              <div key={t}>{TILE_LABELS[t]}: {n}</div>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto bg-gray-950 p-4">
          <svg
            ref={svgRef}
            width={svgW}
            height={svgH}
            viewBox={`0 0 ${svgW} ${svgH}`}
            className="cursor-crosshair block"
            style={{ minWidth: svgW, minHeight: svgH }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseLeave={stopPaint}
          >
            {/* Pass 1: fills */}
            {map.tiles.map((row, r) => row.map((type, c) => (
              <polygon
                key={`f-${r}-${c}`}
                points={triVerts(r, c, b).map(v => v.join(',')).join(' ')}
                fill={TILE_COLORS[type]}
              />
            )))}

            {/* Pass 2: edges (drawn after fills so always visible) */}
            {map.tiles.map((row, r) => row.map((_, c) =>
              triEdges(r, c, b).map((edge, i) => (
                <line
                  key={`e-${r}-${c}-${i}`}
                  x1={edge.x1} y1={edge.y1} x2={edge.x2} y2={edge.y2}
                  stroke={edge.color}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                />
              ))
            ))}

            {/* Pass 3: labels */}
            {map.tiles.map((row, r) => row.map((type, c) => {
              const abbr = TILE_ABBR[type];
              if (!abbr) return null;
              const [cx, cy] = centroid(triVerts(r, c, b));
              return (
                <text
                  key={`l-${r}-${c}`}
                  x={cx} y={cy}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={b * 0.22}
                  fontWeight="bold"
                  fill={type === 'wall' ? '#9ca3af' : '#fff'}
                  style={{ pointerEvents: 'none' }}
                >
                  {abbr}
                </text>
              );
            }))}
          </svg>
        </div>
      </div>

      {/* ── Modals ── */}

      {modal === 'load' && (
        <Modal title="Load Map" onClose={() => setModal(null)}>
          {savedMaps.length === 0 ? (
            <p className="text-gray-400 text-sm">No saved maps yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {savedMaps.map(sm => (
                <div key={sm.name} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{sm.name}</div>
                    <div className="text-xs text-gray-400">
                      {sm.map.cols}×{sm.map.rows} · {new Date(sm.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleLoad(sm)}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-500 rounded text-xs shrink-0"
                  >Load</button>
                  <button
                    onClick={() => handleDelete(sm.name)}
                    className="px-2 py-1 bg-red-700 hover:bg-red-600 rounded text-xs shrink-0"
                  >Del</button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {modal === 'export' && (
        <Modal title="Export Map JSON" onClose={() => setModal(null)}>
          <textarea
            readOnly
            value={exportJson}
            className="w-full h-64 bg-gray-900 text-gray-200 text-xs font-mono p-2 rounded border border-gray-600 resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(exportJson)}
              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm"
            >Copy to Clipboard</button>
            <button
              onClick={handleDownload}
              className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm"
            >Download .json</button>
          </div>
        </Modal>
      )}

      {modal === 'import' && (
        <Modal title="Import Map JSON" onClose={() => setModal(null)}>
          <p className="text-sm text-gray-400">Paste map JSON below, or upload a .json file.</p>
          <textarea
            value={importText}
            onChange={e => setImport(e.target.value)}
            placeholder={'{"name":"...","cols":10,"rows":6,"tiles":[...]}'}
            className="w-full h-48 bg-gray-900 text-gray-200 text-xs font-mono p-2 rounded border border-gray-600 resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => handleImport(importText)}
              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm"
            >Import JSON</button>
            <label className="flex-1 px-3 py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm text-center cursor-pointer">
              Upload File
              <input
                ref={fileRef}
                type="file"
                accept=".json"
                onChange={handleFileLoad}
                className="hidden"
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Shared modal shell ────────────────────────────────────────────────────────

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-5 w-[500px] max-h-[80vh] flex flex-col gap-3 shadow-xl">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto">{children}</div>
        <button
          onClick={onClose}
          className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm self-end mt-1"
        >Close</button>
      </div>
    </div>
  );
}
